import Immutable from 'immutable';
import { getRulesFromState, pageRulesStateToProps, get, pageContext } from 'ctm-util-functions';
import moment from 'moment';
import { prefillPayloadSelector } from 'ctm-selectors/selectors/user';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';

class ProgressionModel {

    constructor(progressionSchema) {
        this.progressionSchema = progressionSchema;
    }

    /**
     * Return the progression schema for the specified page path
     *
     * NOTE: We may not need this, but this is future proofing in case we need to load this dynamically from the backend
     * @param {String} pageName - The name for the page that we want to check conditions for
     * @returns {Object|null} - The progression schema for pagePath, or null if there is no schema specified
     */
    getPageProgressionSchema = (pageName) => {
        return this.progressionSchema[pageName] || null;
    };

    /**
     * Return the resolved rules for the page. Note: these are NOT the same as the page validation rules which you can get
     * using ctm-util-functions/rules/createAddPageRules() which are the validation rules for each element
     * on the page. These rules are the rules that have been specifically set up on a page component.
     * @param {Object} pageProgressionSchema - The progression schema for the next requested page
     * @param {Object} state - The app state
     * @returns {Object} rules - The resolved rules for the page at pagePath
     */
    getProgressionRulesForPage = (pageProgressionSchema, state) => {
        if (!pageProgressionSchema.rule) {
            throw new Error(`There is a progression schema, but no progression rule defined for ${pageProgressionSchema}`);
        }

        return getRulesFromState(state, { rules: [pageProgressionSchema.rule] });
    };

    /**
     * Determines if a page is in the progression model.
     * @param {String} pageName The page name
     * @returns {Boolean} True if the page name is a key in the progression schema.
     */
    isPageConditional = (pageName) => {
        return Object.keys(this.progressionSchema).includes(pageName);
    };

    _updatePrefillLatestPage = (pagePath, schemaAsOrderedList) => {
        const { store } = pageContext.getProps();
        const nextPageName = pageContext.getProps().models.pageSchemaModel.getPageNameForPath(pagePath);
        const firstEligiblePage = getFeatureFlag('firstEligiblePage');
        // Is this page eligible for prefill?
        const eligiblePage = !firstEligiblePage
            ? false
            : schemaAsOrderedList.indexOf(firstEligiblePage) <= schemaAsOrderedList.indexOf(nextPageName);

        store.dispatch({
            type: 'SET_VALUE',
            value: pagePath,
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.lastViewedPage'
        });
        store.dispatch({
            type: 'SET_VALUE',
            value: eligiblePage,
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.eligiblePage'
        });
    };

    /**
     * Get the next page path, based on if there are any progression rules for the requested next page and the results
     * of those rules if they exist.
     *
     * If there are no progression rules: return the currentNextPagePath as we're allowed to progress to it
     * If there are progression rules and they have passed: return the currentNextPagePath as we're allowed to progress to it
     * If there are progression rules, but they have failed: return the url specified as 'redirectForUnmetCondition' in the
     * progression schema for 'currentNextPagePath'
     *
     * This is used because the state can change after the NextStepButton is mounted.
     * The component doesn't know which URL to redirect to at time of mounting if its conditional based on filling out another value.
     * @param {String} currentNextPagePath - The path of the requested next page
     * @param {Object} state - The current application state
     * @returns {String} - The page path to redirect to
     */
    getNextPage = (currentNextPagePath, state) => {
        const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
        const nextPageName = pageSchemaModel.getPageNameForPath(currentNextPagePath);
        const schema = this.getPageProgressionSchema(nextPageName);
        const schemaAsOrderedList = pageContext.getProps().models.pageSchemaModel.schemaAsOrderedList;
        const prevStatePageName = state.getIn(['page', 'name']);

        if (!schema) {
            const browserHistory = pageContext.getProps().browserHistory;
            const lastViewedPageName = state.getIn(['page', 'lastViewedPage']);
            const isManualJourney = state.getIn(['page', 'isManualLookup']);

            // Car - For Back arrow on welcome_back page, if last viewed page is car_colour page
            // navigate to car_series page for manual journey and to car_rego_state for non manual journey instead of car_colour page
            if (prevStatePageName === 'welcome_back' && browserHistory.action === 'POP' && lastViewedPageName === 'car_colour') {
                const nextPagePath = isManualJourney ? pageSchemaModel.getPathForPageName('car_series') : pageSchemaModel.getPathForPageName('car_rego_state');
                this._updatePrefillLatestPage(nextPagePath, schemaAsOrderedList);
                return nextPagePath;
            }
            this._updatePrefillLatestPage(currentNextPagePath, schemaAsOrderedList);
            return currentNextPagePath;
        }

        // Figure out if we are going back or forward - if we're going back and it's to another conditional page
        // we need to check if we can reach that conditional page. If not we need to go to the page before that conditional page
        // and try again until we hit a page that can be loaded.
        const isGoingBack = !pageSchemaModel.isLaterPageInSchema(prevStatePageName, currentNextPagePath);
        const rules = this.getProgressionRulesForPage(schema, state);
        if (get(rules, 'allowProgressIfTrue.result', false)) {
            this._updatePrefillLatestPage(currentNextPagePath, schemaAsOrderedList);
            return currentNextPagePath;
        } if (isGoingBack) {
            // we're going back to a conditional page, but the rules for displaying that conditional page are not met
            // in this case, try to go to the page before the conditional page
            this._updatePrefillLatestPage(pageSchemaModel.getPreviousPageSchema(currentNextPagePath).path, schemaAsOrderedList);
            return pageSchemaModel.getPreviousPageSchema(currentNextPagePath).path;
        }
        // Unmet condition redirect
        this._updatePrefillLatestPage(pageSchemaModel.getPathForPageName(schema.redirectForUnmetCondition), schemaAsOrderedList);
        return pageSchemaModel.getPathForPageName(schema.redirectForUnmetCondition);
    };

    /**
     * Retrieve the next page link, which will be:
     *      results - if autoProgressToResults is true and no seenResultsNextPageName is defined
     *      seenResultsNextPageName - if autoProgressTo results is true and seenResultsNextPageName is defined
     *      the defined nextStepButton.link prop.
     * @param {Object} props The properties
     * @param {String} qualifier The apps page schema settings.qualifier
     * @returns {String} The next page URL
     */
    getNextPageLink = (props, qualifier) => {
        const { nextPageName, splitTestNextPage, seenResultsNextPageName, hasSeenConfirmationPage } = props;
        const canAutoProgressToResults = get(props, 'rules.autoProgressToResults.result');
        const canProgressToSplitTestPage = get(props, 'rules.progressToSplitTestPage.result');
        const canProgressToVerifyPage = get(props, 'rules.progressToVerifyPage.result');
        const canProgressToQuotesExpiredPage = get(props, 'rules.progressToQuotesExpiredPage.result');
        const progressToCarMake = get(props, 'rules.progressToCarMake.result');
        const currentPageName = get(props, 'pageName');

        const canAutoProgressToPrefill = get(props, 'rules.autoProgressToPrefill.result');
        const canProgressToHNCWelcomeBack = get(props, 'rules.progressToWelcomeBack.result');

        if (canAutoProgressToResults) {
            const nextInvalidPage = this.getNextInvalidPageName(props.pageName);
            const currentDate = moment().format('YYYY-MM-DD');
            const currentCommencementDate = pageContext.getProps().store.getState().getIn(['vertical', 'quote', 'form', 'coverDetail', 'commencementDate']);
            const isCommencementDateInPast = moment(currentCommencementDate).isBefore(currentDate);

            const prefill = prefillPayloadSelector(pageContext.getProps().store.getState());

            if (!nextInvalidPage) {
                if (seenResultsNextPageName) {
                    return `${qualifier}/${seenResultsNextPageName}`;
                }
                if (hasSeenConfirmationPage) {
                    return `${qualifier}/confirmation`;
                }
                if (isCommencementDateInPast && prefill && nextPageName === 'results') {
                    return `${qualifier}/commencement_date`;
                }
                return `${qualifier}/results`;
            }
            return `${qualifier}/${nextInvalidPage}`;
        }
        if (canProgressToSplitTestPage) {
            return `${qualifier}/${splitTestNextPage}`;
        }

        if (canProgressToVerifyPage) {
            return `${qualifier}/verify`;
        }

        if (canProgressToQuotesExpiredPage) {
            return `${qualifier}/quotes_expired`;
        }

        if (canAutoProgressToPrefill) {
            return `${qualifier}/confirmation`;
        }

        if (progressToCarMake && nextPageName === 'rego_state' && currentPageName === 'start') {
            // Condition for when MVL Test 4 is running and the user
            // Goes through the manual vehicle lookup route
            return `${qualifier}/car_make`;
        }
        // HnC - Welcome back page
        if (canProgressToHNCWelcomeBack && nextPageName === 'occupancy' && currentPageName === 'start') {
            return `${qualifier}/welcome_back`;
        }
        return `${qualifier}/${nextPageName}`;
    };

    /**
     * Based on a current page name, find the next page name that is invalid based on a new state test, up to results page.
     * @param {String} pageName The current page name
     * @returns {String|Null} Null if all pages are valid and the page name to skip to if there's an invalid one.
     */
    getNextInvalidPageName = (pageName) => {
        const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
        const validationModel = pageContext.getProps().models.validationModel;
        const nextPageNames = pageSchemaModel.getNextPagesUpToResults(pageName);
        const state = pageContext.getProps().store.getState();
        let nextInvalidPage = null;
        for (const name of nextPageNames) {
            const pageRules = pageRulesStateToProps(name, 'validation')(state).rules;
            const visibility = pageRulesStateToProps(name, 'visibility')(state).rules;
            // Dynamically resolving the visibility rules for the next page because
            // the stored visibility state doesn't take into account things that haven't been mounted yet.
            const resolvedVisibilityRules = Object.keys(visibility).reduce((acc, val) => {
                return acc.set(val, visibility[val].visibility.result);
            }, Immutable.Map());
            if (!validationModel.validatePage(name, pageRules, resolvedVisibilityRules).isValid) {
                nextInvalidPage = name;
                break;
            }

            // Check if the page is a loading page and if so, check the loading rule
            // This is required because the loading rule is not part of the validation rules and needs to be checked separately
            const loading = pageRulesStateToProps(name, 'loading')(state).rules;
            if (loading && Object.values(loading).some((rule) => get(rule, 'loading.result', false))) {
                nextInvalidPage = name;
                break;
            }
        }
        return nextInvalidPage;
    };

}

export { ProgressionModel };
